import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Entry } from '../utils/persistenceService';

interface CreateEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (entry: Omit<Entry, '_id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
}

const CreateEntryModal: React.FC<CreateEntryModalProps> = ({ isOpen, onClose, onSave }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [type, setType] = useState<'manual' | 'reflection'>('manual');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!content.trim()) {
      setError('Entry content is required');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const entry: Omit<Entry, '_id' | 'createdAt' | 'updatedAt'> = {
        date: new Date().toISOString().slice(0, 10),
        type,
        content: {
          title: title.trim() || undefined,
          body: content.trim()
        },
        metadata: {
          wordCount: content.trim().split(/\s+/).length
        }
      };

      await onSave(entry);
      
      // Reset form
      setTitle('');
      setContent('');
      setType('manual');
      onClose();
    } catch (err) {
      console.error('Failed to save entry:', err);
      setError('Failed to save entry. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSave();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div 
        className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800">Create New Entry</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Entry Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Entry Type
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="type"
                  value="manual"
                  checked={type === 'manual'}
                  onChange={() => setType('manual')}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Manual Entry</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="type"
                  value="reflection"
                  checked={type === 'reflection'}
                  onChange={() => setType('reflection')}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Reflection</span>
              </label>
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title (Optional)
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-colors"
              placeholder="Give your entry a title..."
              maxLength={200}
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full min-h-[200px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-colors resize-none"
              placeholder="What's on your mind? Reflect on your progress, thoughts, or insights..."
              rows={8}
            />
            <div className="flex justify-between items-center mt-2">
              <div className="text-xs text-gray-400">
                {content.trim().split(/\s+/).filter(word => word.length > 0).length} words
              </div>
              <div className="text-xs text-gray-400">
                ⌘+Enter to save
              </div>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="text-red-800 text-sm">{error}</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading || !content.trim()}
            className="px-6 py-2 bg-rose-500 hover:bg-rose-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
          >
            {isLoading ? 'Saving...' : 'Save Entry'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateEntryModal;